/**
 * Content autopilot — THE MANDATE, verbatim: "Our content needs to be
 * stupid simple, automated even. Doing research, coming up with ideas,
 * drafting on its own."
 *
 * One daily run (riding the EXISTING live-schedule tick via the
 * automations registry — see src/lib/agent/automations.ts) does the whole
 * research → ideas → scored drafts loop:
 *
 *   IDEAS  — 3–5 idea seeds derived from REAL context only: Brain facts
 *            (voice/ICP/positioning), recent wins (deriveRecentWins),
 *            at-risk saves, with angle variety rotated deterministically
 *            by weekday (callers inject `now` — no Date.now() in the run).
 *   DRAFTS — the shipped preppedDrafts composers turn those seeds into
 *            full drafts across types (LinkedIn post, X post, blog
 *            outline…), skipping anything whose title already exists.
 *   SCORE  — each draft is born-scored via scoreContent: the REAL AQI
 *            engine when configured, the honest deterministic simulation
 *            otherwise — the `simulated` flag is kept on the item, never
 *            laundered into a real-looking number.
 *   LAND   — drafts land as contentItems in the Idea/Draft pipeline
 *            tagged 'autopilot', plus exactly ONE feed entry
 *            ("Content autopilot: drafted 4 posts — 2 scored 80+").
 *
 * Honesty contract:
 *   • drafting is SAFE automation — nothing here sends, schedules, or
 *     publishes; every artifact is a draft behind the existing gates;
 *   • an EMPTY Brain returns { needsSetup: true } and the feed/greeting
 *     line becomes the setup ask — never fake content;
 *   • everything is composed from records that exist; provenance rides
 *     along from the preppedDrafts generator.
 *
 * `runContentAutopilot` is dependency-injected (data + clock + writers)
 * so tests are deterministic; `runContentAutopilotFromStore` wires the
 * real store/scorer/feed for the automations registry.
 */

import type {
  BrainFact,
  ContentItem,
  ContentItemType,
  Deliverable,
  Person,
} from '../db/types';
import type { AqiScoreResult, ContentChannel } from '../agent/contentEngine';
import { scoreContent } from '../agent/contentEngine';
import { channelForType } from './pipeline';
import {
  deriveRecentWins,
  preparedDrafts,
  type PreppedSlot,
  type RecentWin,
} from './preppedDrafts';
import { db } from '../db/store';
import { createContentItem, listContentItems } from '../db/contentItems';
import { appendFeedEntry, type NewFeedEntry } from '../agent/activityFeed';

// ---------------------------------------------------------------------------
// Tags + copy
// ---------------------------------------------------------------------------

/** templateId prefix stamped on every autopilot-drafted item. */
export const AUTOPILOT_TEMPLATE_PREFIX = 'autopilot:';

/** Feed identity for the autopilot's single per-run entry. */
export const AUTOPILOT_AGENT_ID = 'automation:content-autopilot';
export const AUTOPILOT_AGENT_NAME = 'Content autopilot';

/** A draft counts as "scored high" at or above this AQI total. */
export const AUTOPILOT_HIGH_SCORE = 80;

/** The honest empty-Brain line — the output IS the setup ask. */
export const AUTOPILOT_SETUP_ASK =
  'Content autopilot: nothing to draft yet — add Company Brain facts ' +
  '(voice, ICP, positioning) and Argo will research, ideate, and draft daily.';

/** Was this item drafted by the autopilot? */
export function isAutopilotItem(item: Pick<ContentItem, 'templateId'>): boolean {
  return Boolean(item.templateId?.startsWith(AUTOPILOT_TEMPLATE_PREFIX));
}

// ---------------------------------------------------------------------------
// IDEAS — 3–5 seeds from real context, weekday-rotated angles (pure)
// ---------------------------------------------------------------------------

/** One idea seed: a real-context slot plus the weekday-rotated angle. */
export interface IdeaSeed {
  slot: PreppedSlot;
  type: ContentItemType;
  /** The angle this weekday's rotation picked — a concrete working title. */
  angle: string;
  /** Human-readable provenance — the REAL facts behind the idea. */
  grounding: string[];
  /** 0 (Sun) – 6 (Sat), UTC — the deterministic rotation input. */
  weekday: number;
}

/** UTC weekday of `now` — the deterministic variety knob. */
export function autopilotWeekday(now: number): number {
  return new Date(now).getUTCDay();
}

/** Every slot rotated to this weekday's angle variant. Pure. */
export function weekdayVariants(now: number): Partial<Record<PreppedSlot, number>> {
  const w = autopilotWeekday(now);
  return {
    'customer-win': w,
    'positioning-linkedin': w,
    'x-take': w,
    'icp-pain-blog': w,
    'recovery-story': w,
  };
}

/**
 * Derive up to five idea seeds from REAL context: Brain facts, recent
 * wins, persona pains, and at-risk saves — the same grounded slots the
 * preppedDrafts generator composes from, with the day-of-week rotating
 * which angle each slot takes. Empty Brain → [] (nothing is invented).
 * Deterministic: same inputs + same `now` day ⇒ same seeds.
 */
export function deriveIdeaSeeds(
  brainFacts: BrainFact[],
  people: Person[],
  wins: RecentWin[],
  now: number,
): IdeaSeed[] {
  const weekday = autopilotWeekday(now);
  return preparedDrafts(brainFacts, people, wins, [], weekdayVariants(now)).map(
    (spec) => ({
      slot: spec.slot,
      type: spec.type,
      angle: spec.title,
      grounding: spec.grounding,
      weekday,
    }),
  );
}

// ---------------------------------------------------------------------------
// The run — ideas → drafts → born scores → landed items + ONE feed entry
// ---------------------------------------------------------------------------

/** Everything a run needs, injected — no clock/store/network reads here. */
export interface AutopilotDeps {
  /** Injected clock (ms epoch) — weekday rotation + timestamps. */
  now: number;
  brainFacts: BrainFact[];
  people: Person[];
  deliverables: Deliverable[];
  /** Current pipeline — dedupe target (autopilot never re-drafts a title). */
  existingItems: ContentItem[];
  /** The born-scorer: real AQI when configured, honest simulation otherwise. */
  score: (input: { content: string; channel: ContentChannel }) => Promise<AqiScoreResult>;
  /** Lands one draft in the pipeline (the contentItems store API). */
  createItem: (input: {
    title: string;
    type: ContentItemType;
    status: 'draft';
    body: string;
    score: number;
    scoreSimulated: boolean;
    templateId: string;
  }) => Promise<ContentItem>;
  /** Appends the run's single feed entry. Best-effort. */
  appendFeed: (entry: NewFeedEntry) => Promise<unknown>;
}

/** What a run did — counts for the feed line + the session greeting. */
export interface AutopilotResult {
  /** True ⇔ the Brain is empty: no drafts, the summary IS the setup ask. */
  needsSetup: boolean;
  seeds: IdeaSeed[];
  /** Items actually landed this run. */
  items: ContentItem[];
  drafted: number;
  /** How many of this run's drafts scored ≥ AUTOPILOT_HIGH_SCORE. */
  scoredHigh: number;
  /** Ideas skipped because their title already exists in the pipeline. */
  skippedDuplicates: number;
  /** The one honest line — feed entry + session greeting share it. */
  summary: string;
}

const normTitle = (t: string) => t.trim().toLowerCase();

/** PURE: run counts → the canonical autopilot line. */
export function autopilotSummary(
  drafted: number,
  scoredHigh: number,
  skippedDuplicates: number,
): string {
  if (drafted === 0) {
    return (
      `Content autopilot: nothing new to draft — ` +
      `${skippedDuplicates} idea${skippedDuplicates === 1 ? '' : 's'} already in your pipeline`
    );
  }
  return (
    `Content autopilot: drafted ${drafted} post${drafted === 1 ? '' : 's'} — ` +
    `${scoredHigh} scored ${AUTOPILOT_HIGH_SCORE}+`
  );
}

/**
 * One autopilot run: ideas → drafts → born scores → landed items, plus
 * exactly ONE feed entry. Empty Brain → { needsSetup: true } with ZERO
 * items and the setup ask as the line — never fabricated content.
 * Nothing here (or downstream) sends or publishes anything.
 */
export async function runContentAutopilot(deps: AutopilotDeps): Promise<AutopilotResult> {
  const ts = new Date(deps.now).toISOString();
  const feed = (summary: string) =>
    deps
      .appendFeed({
        agentId: AUTOPILOT_AGENT_ID,
        agentName: AUTOPILOT_AGENT_NAME,
        summary,
        proposalsQueued: 0, // drafts are safe — nothing awaits a send gate
        trigger: 'schedule',
        ts,
      })
      .catch(() => undefined);

  // EMPTY BRAIN — the honest output is the setup ask, not fake drafts.
  if (deps.brainFacts.length === 0) {
    await feed(AUTOPILOT_SETUP_ASK);
    return {
      needsSetup: true,
      seeds: [],
      items: [],
      drafted: 0,
      scoredHigh: 0,
      skippedDuplicates: 0,
      summary: AUTOPILOT_SETUP_ASK,
    };
  }

  // IDEAS — real context only, weekday-rotated angles.
  const wins = deriveRecentWins(deps.deliverables, deps.people);
  const seeds = deriveIdeaSeeds(deps.brainFacts, deps.people, wins, deps.now);

  // DRAFTS — the shipped composers over those seeds, deduped vs the
  // pipeline (case-insensitive title match, counted honestly).
  const specs = preparedDrafts(
    deps.brainFacts,
    deps.people,
    wins,
    [],
    weekdayVariants(deps.now),
  );
  const existingTitles = new Set(deps.existingItems.map((i) => normTitle(i.title)));
  const fresh = specs.filter((s) => !existingTitles.has(normTitle(s.title)));
  const skippedDuplicates = specs.length - fresh.length;

  // SCORE + LAND — born-scored drafts into the Idea/Draft pipeline.
  const items: ContentItem[] = [];
  let scoredHigh = 0;
  for (const spec of fresh) {
    const aqi = await deps.score({
      content: spec.body,
      channel: channelForType(spec.type),
    });
    if (aqi.total >= AUTOPILOT_HIGH_SCORE) scoredHigh += 1;
    items.push(
      await deps.createItem({
        title: spec.title,
        type: spec.type,
        status: 'draft', // full body → Draft lane; publishing stays gated
        body: spec.body,
        score: aqi.total,
        scoreSimulated: aqi.simulated, // the honest 'Simulated' pill survives
        templateId: `${AUTOPILOT_TEMPLATE_PREFIX}${spec.slot}`,
      }),
    );
  }

  const summary = autopilotSummary(items.length, scoredHigh, skippedDuplicates);
  await feed(summary); // exactly ONE entry per run
  return {
    needsSetup: false,
    seeds,
    items,
    drafted: items.length,
    scoredHigh,
    skippedDuplicates,
    summary,
  };
}

// ---------------------------------------------------------------------------
// Store wiring — what the automations registry actually calls
// ---------------------------------------------------------------------------

/**
 * Synthetic org-scoped rows (Heartbeat state etc.) live in `brainFacts`
 * under this account id — they are bookkeeping, not Brain knowledge, so
 * the autopilot must not mistake them for a set-up Brain.
 */
const ORG_ACCOUNT_ID = '__org__';

/**
 * Run the autopilot against the REAL store: live Brain facts (synthetic
 * org rows excluded), people, deliverables, and the current pipeline;
 * scoreContent for born scores; createContentItem to land drafts;
 * appendFeedEntry for the single feed line.
 */
export async function runContentAutopilotFromStore(now: number): Promise<AutopilotResult> {
  const [facts, people, deliverables, existingItems] = await Promise.all([
    db.list('brainFacts'),
    db.list('people'),
    db.list('deliverables'),
    listContentItems(),
  ]);
  return runContentAutopilot({
    now,
    brainFacts: facts.filter((f) => f.accountId !== ORG_ACCOUNT_ID),
    people,
    deliverables,
    existingItems,
    score: (input) => scoreContent(input),
    createItem: (input) => createContentItem(input),
    appendFeed: (entry) => appendFeedEntry(entry),
  });
}
